// src/models/TeamModel.ts
import { teamUrl } from "../utils/canonicalUrl";

/**
 * Team model - a sport-specific branch of a Club.
 * Example: Beşiktaş Erkek Futbol (club: Beşiktaş, sportType: FOOTBALL, gender: MALE).
 * A club can have multiple teams in the same sport+gender combination, distinguished by teamCode.
 * Includes embedded club info so the UI doesn't need a second API call.
 */
export interface TeamModel {
  id: number;
  // Custom display name if set (e.g. "Fenerbahçe Beko"); null means use clubShortName
  displayName: string | null;
  sportType: string;            // "FOOTBALL", "BASKETBALL", etc.
  gender: string;               // "MALE", "FEMALE", "MIXED"
  // Sub-team code: null = main/single team, "a", "1b", "u19", "paf", etc.
  // Appended to the URL slug: /takim/besiktas/futbol-1b
  teamCode: string | null;
  active: boolean;
  // Embedded club info
  clubId: number;
  clubShortName: string;        // "Beşiktaş"
  clubLongName: string | null;  // "Beşiktaş Jimnastik Kulübü"
  clubAbbreviation: string | null; // "BJK"
  clubSlug: string;             // "besiktas"
  clubLogoUrl: string | null;
  clubCountry: string;          // "TR"
  clubCity: string | null;
  // Parent club brand colors in #RRGGBB format — used to paint the team page
  // banner gradient so a team visually belongs to the same club family.
  clubColor1: string | null;
  clubColor2: string | null;
  clubColor3: string | null;
  // Number of entries written about this team today, counted from local
  // midnight — resets at 00:00 (public endpoint only)
  recentEntryCount?: number;
  // Whether new entry submissions are currently blocked for this team's page
  isLocked?: boolean;
  // Admin's note explaining the lock — only present when marked visible to users
  lockReason?: string | null;
  // Whether lockReason is shown to regular users (admin panel only)
  lockReasonVisible?: boolean;
  // Who applied the current lock and when (admin panel only)
  lockedByUsername?: string | null;
  lockedAt?: string | null;
}

// Helper: returns the best display name for a team
// Uses displayName if set, otherwise falls back to clubShortName
export const getTeamDisplayName = (team: TeamModel): string => {
  return team.displayName ?? team.clubShortName;
};

/** Sport type → Turkish URL slug mapping */
const SPORT_SLUG_MAP: Record<string, string> = {
  FOOTBALL:          "futbol",
  BASKETBALL:        "basketbol",
  VOLLEYBALL:        "voleybol",
  AMERICAN_FOOTBALL: "amerikan-futbol",
  BASEBALL:          "beyzbol",
  ICE_HOCKEY:        "buz-hokeyi",
  HANDBALL:          "hentbol",
};

/**
 * Build the {sportSlug}[-gender][-teamCode] portion of a team URL slug from
 * primitive fields, rather than a full TeamModel. Extracted so callers that
 * only have loose sportType/gender/teamCode values — e.g. a match's home/away
 * side, which carries these at the match level rather than as a nested
 * TeamModel — can build the same slug without constructing a fake TeamModel.
 * Examples:
 *   FOOTBALL / MALE   / null  → "futbol"
 *   FOOTBALL / MALE   / "1b"  → "futbol-1b"
 *   FOOTBALL / FEMALE / null  → "futbol-kadin"
 *   FOOTBALL / FEMALE / "u19" → "futbol-kadin-u19"
 */
export const buildTeamUrlSlug = (
  sportType: string,
  gender: string,
  teamCode?: string | null
): string => {
  const sport    = SPORT_SLUG_MAP[sportType] ?? sportType.toLowerCase();
  const genderSlug = gender === "FEMALE" ? "-kadin" : gender === "MIXED" ? "-karma" : "";
  const codeSlug  = teamCode ? `-${teamCode}` : "";
  return `${sport}${genderSlug}${codeSlug}`;
};

/**
 * Generate the full URL slug for a team.
 * Format: {sportSlug}[-gender][-teamCode]
 */
export const getTeamUrlSlug = (team: TeamModel): string => {
  return buildTeamUrlSlug(team.sportType, team.gender, team.teamCode);
};

/**
 * Build the full team page URL path.
 * Format: /takim/{clubSlug}-{teamSlug}--{teamId}
 * Examples:
 *   /takim/fenerbahce-futbol--9
 *   /takim/fenerbahce-futbol-1b--14
 *   /takim/galatasaray-basketbol-kadin--21
 *   /takim/galatasaray-basketbol-kadin-u19--22
 *
 * One segment, matching the other four domains. Resolution goes through the
 * trailing id, so renaming a club changes the readable part without breaking
 * links that were already shared.
 */
export const getTeamPagePath = (team: TeamModel): string => {
  return teamUrl(team.clubSlug, getTeamUrlSlug(team), team.id);
};

/**
 * Build the team page URL path from primitive fields instead of a full
 * TeamModel — see buildTeamUrlSlug for why this variant exists.
 *
 * Emits the legacy two-segment, id-less form: callers of this variant only have
 * the parts, not the team's id. Still resolves — the backend answers 301 with
 * the canonical address, which the page then navigates to.
 */
export const getTeamPagePathFromParts = (
  clubSlug: string,
  sportType: string,
  gender: string,
  teamCode?: string | null
): string => {
  return `/takim/${clubSlug}/${buildTeamUrlSlug(sportType, gender, teamCode)}`;
};

/**
 * Request to create a new team branch under an existing club.
 */
export interface CreateTeamRequest {
  clubId: number;
  sportType: string;
  gender: string;
  // Optional sub-team code: null/blank = main team, "a", "1b", "u19", etc.
  teamCode?: string | null;
}

/**
 * Request to update a team (only active flag is mutable).
 */
export interface UpdateTeamRequest {
  active?: boolean;
}